import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, MenuItem, Switch, TextField, Typography,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import api from "../lib/api";
import { authFieldSx } from "../pages/authFieldStyles";

const emptyForm = {
    name: "", country: "", region: "", crop: "", variety: "", acres: "",
    latitude: "", longitude: "", is_public: true,
};

const countries = ["Egypt", "Jordan", "Morocco", "Tunisia"];
const crops = ["Barley", "Oat", "Wheat"];
const normalizeCrop = (value) => crops.find(
    (crop) => crop.toLowerCase() === String(value || "").trim().toLowerCase()
) || "";

const selectMenuProps = {
    PaperProps: {
        sx: {
            bgcolor: "#fff",
            border: "1px solid #e8e8e8",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
            "& .MuiMenuItem-root": {
                backgroundColor: "#fff !important",
                outline: "none !important",
                boxShadow: "none !important",
                border: "none",
                "&:focus, &:focus-visible, &.Mui-focusVisible": {
                    outline: "none !important",
                    boxShadow: "none !important",
                    backgroundColor: "#fffaf0 !important",
                },
                "&:hover": { backgroundColor: "#fffaf0 !important" },
                "&.Mui-selected": { backgroundColor: "#fff7d6 !important" },
                "&.Mui-selected:hover, &.Mui-selected:focus, &.Mui-selected:focus-visible, &.Mui-selected.Mui-focusVisible": {
                    outline: "none !important",
                    boxShadow: "none !important",
                    backgroundColor: "#ffefb0 !important",
                },
            },
        },
    },
};

const fieldSx = {
    ...authFieldSx,
    "& .MuiInputLabel-root": { color: "#000" },
    "& .MuiInputBase-input": { color: "#000" },
    "& .MuiSelect-select": { color: "#000" },
    "& input[type=number]": { MozAppearance: "textfield" },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
        WebkitAppearance: "none", margin: 0,
    },
};

const yellowButtonSx = {
    "&&": {
        bgcolor: "#f7b900", backgroundImage: "none", color: "#312600",
        borderColor: "#f7b900", boxShadow: "none", fontWeight: 600,
        textTransform: "none",
    },
    "&&:hover, &&:focus-visible": {
        bgcolor: "#dca600", color: "#312600", borderColor: "#dca600", boxShadow: "none",
    },
    "&&.Mui-disabled": {
        bgcolor: "#f7b900", color: "#312600", opacity: 0.42, boxShadow: "none",
    },
};

const toNumOrNull = (value) => {
    const clean = String(value ?? "").trim();
    if (!clean) return null;
    const number = Number(clean);
    return Number.isFinite(number) ? number : null;
};

export default function AddFieldDialog({ open, onClose, onCreated, onSaved, isOwner, field }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [err, setErr] = useState("");
    const isEditing = Boolean(field?.id);

    useEffect(() => {
        if (!open) return;
        setErr("");
        setForm(field ? {
            ...emptyForm,
            ...field,
            crop: normalizeCrop(field.crop),
            acres: field.acres ?? "",
            latitude: field.latitude ?? "",
            longitude: field.longitude ?? "",
        } : emptyForm);
    }, [field, open]);

    const canSubmit = useMemo(
        () => [form.name, form.country, form.region, form.crop].every((v) => String(v).trim()) && isOwner && !saving,
        [form, isOwner, saving],
    );

    const update = (key) => (event) => setForm((previous) => ({ ...previous, [key]: event.target.value }));
    const updateAcres = (event) => {
        const value = event.target.value;
        if (value === "" || /^\d*\.?\d*$/.test(value)) {
            setForm((previous) => ({ ...previous, acres: value }));
        }
    };

    const onSave = async () => {
        setErr("");
        if (!canSubmit) {
            setErr("Field name, country, region and crop are required.");
            return;
        }

        const acres = toNumOrNull(form.acres);
        const latitude = toNumOrNull(form.latitude);
        const longitude = toNumOrNull(form.longitude);
        if (acres != null && acres <= 0) {
            setErr("Acres must be a positive number.");
            return;
        }
        if (latitude != null && (latitude < -90 || latitude > 90)) {
            setErr("Latitude must be between -90 and 90.");
            return;
        }
        if (longitude != null && (longitude < -180 || longitude > 180)) {
            setErr("Longitude must be between -180 and 180.");
            return;
        }
        const payload = {
            name: form.name.trim(), country: form.country, region: form.region.trim(),
            crop: form.crop, variety: form.variety.trim(), acres,
            latitude, longitude,
            is_public: Boolean(form.is_public),
        };

        setSaving(true);
        try {
            if (isEditing) await api.patch(`/fields/mine/${field.id}/`, payload);
            else await api.post("/fields/mine/", payload);
            await onCreated?.();
            onSaved?.({ isEditing, name: payload.name });
            onClose?.();
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            setErr(data?.detail || (data && typeof data === "object" ? JSON.stringify(data) : "Failed to save field."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 700 }}>{isEditing ? "Edit field" : "Add new field"}</DialogTitle>
            <DialogContent dividers>
                {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Field details</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <TextField required label="Field name" value={form.name} onChange={update("name")} fullWidth sx={fieldSx} />
                    <TextField required select label="Country" value={form.country} onChange={update("country")} fullWidth sx={fieldSx}
                        SelectProps={{ IconComponent: KeyboardArrowDownRoundedIcon, MenuProps: selectMenuProps }}>
                        {countries.map((country) => <MenuItem key={country} value={country}>{country}</MenuItem>)}
                    </TextField>
                    <TextField required label="Region" value={form.region} onChange={update("region")} fullWidth sx={fieldSx} />
                    <TextField required select label="Crop" value={form.crop} onChange={update("crop")} fullWidth sx={fieldSx}
                        SelectProps={{ IconComponent: KeyboardArrowDownRoundedIcon, MenuProps: selectMenuProps }}>
                        {crops.map((crop) => <MenuItem key={crop} value={crop}>{crop}</MenuItem>)}
                    </TextField>
                    <TextField label="Variety" value={form.variety} onChange={update("variety")} fullWidth sx={fieldSx} />
                    <TextField label="Acres" type="text" inputMode="decimal" value={form.acres} onChange={updateAcres} fullWidth sx={fieldSx} />
                    <TextField label="Latitude" type="number" value={form.latitude} onChange={update("latitude")} fullWidth sx={fieldSx}
                        inputProps={{ min: -90, max: 90, step: "any" }} />
                    <TextField label="Longitude" type="number" value={form.longitude} onChange={update("longitude")} fullWidth sx={fieldSx}
                        inputProps={{ min: -180, max: 180, step: "any" }} />
                    <Box
                        sx={{
                            gridColumn: "1 / -1",
                            p: 1.5,
                            border: "1px solid #e5e7eb",
                            borderRadius: 2,
                            bgcolor: "#fafafa",
                        }}
                    >
                        <Typography sx={{ fontWeight: 700, color: "#000" }}>
                            Field visibility
                        </Typography>
                        <FormControlLabel
                            control={<Switch checked={Boolean(form.is_public)} onChange={(event) => setForm((p) => ({ ...p, is_public: event.target.checked }))}
                                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#f7b900" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: "#f7b900" } }} />}
                            label={form.is_public ? "Public field" : "Private field"}
                            sx={{ mt: 0.25, color: "#000" }}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {form.is_public
                                ? "Extension Agents and Researchers can view this field and its data in read-only mode."
                                : "This field is hidden from Extension Agents and Researchers."}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit" sx={{ textTransform: "none" }}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={!canSubmit} sx={yellowButtonSx}>
                    {saving ? "Saving..." : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
